import React, { useEffect } from "react";

export interface ImagePreviewModalProps {
  url: string | null;
  onClose: () => void;
}

/**
 * Full-screen dimmed overlay with large image; closes on backdrop click or Escape.
 */
export function ImagePreviewModal({ url, onClose }: ImagePreviewModalProps): React.ReactElement | null {
  useEffect(() => {
    if (url === null) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [url, onClose]);

  if (url === null || url === "") {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        background: "rgba(15, 23, 42, 0.82)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        boxSizing: "border-box",
      }}
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        style={{
          position: "absolute",
          top: 16,
          right: 16,
          width: 44,
          height: 44,
          borderRadius: "var(--radius-sm)",
          border: "1px solid var(--border)",
          background: "var(--panel)",
          color: "var(--ink)",
          fontSize: 20,
          fontWeight: 700,
          cursor: "pointer",
          lineHeight: 1,
        }}
        aria-label="Close preview"
      >
        ×
      </button>
      <div
        role="group"
        aria-label="Enlarged image"
        onClick={(e) => { e.stopPropagation(); }}
        onKeyDown={(e) => { e.stopPropagation(); }}
      >
        <img
          src={url}
          alt="Enlarged preview"
          style={{
            maxWidth: "100%",
            maxHeight: "90vh",
            width: "auto",
            height: "auto",
            objectFit: "contain",
            borderRadius: "var(--radius-sm)",
            boxShadow: "var(--shadow)",
            background: "var(--panel)",
            display: "block",
          }}
        />
      </div>
    </div>
  );
}
